// Cyclopedia: bestiário (abates por criatura, desbloqueios oficiais) e charms (liberar, melhorar, colocar numa criatura).
// Visual de grimório: capa de couro, cantos dourados, abas como marcadores de página. CSS em client/css-parts/cyclo.css (prefixo .cy-).
import { ANIMUS_GENERAL_PCT, ANIMUS_XP_PCT, animusGeneral } from '../../../../shared/src/game/soulpit';
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { AchDef } from '../../../../shared/src/game/achievements';
import { BESTIARY_GOLD_PCT, BESTIARY_KILLS, BESTIARY_XP_PCT, CHARMS, bestiaryStage, type BestiaryInfo, type CharmDef, type CharmState } from '../../../../shared/src/game/charms';
import type { Outfit } from '../../../../shared/src/protocol';
import { Portrait } from './BattleList';
import { ItemImg } from './ItemSlot';
import { Cards } from './CardsTab';

type Tab = 'bestiary' | 'charms' | 'cards' | 'achievements' | 'animus';
let achP: Promise<AchDef[]> | null = null;
const loadAch = () => (achP ??= fetch('/data/achievements.json').then((r) => r.json()));

export interface BestiaryEntry extends BestiaryInfo { name: string; outfit: Outfit; hp: number; exp: number; loot: number[] }
const n = (v: number) => v.toLocaleString('pt-BR');
const pct = (v: number) => (Math.round(v * 1000) / 1000).toString().replace('.', ',') + '%';
const STAGE_NAME = ['Desconhecida', 'Bronze', 'Prata', 'Ouro'];
const STAGE_ROMAN = ['I', 'II', 'III'];

/** Ajuda curta de cada aba (passo a passo) */
function Steps(props: { items: Array<[string, string, string]> }) {
  return <div class="cy-steps">{props.items.map(([i, b, s], k) => <div key={k}><i>{i}</i><b>{k + 1}. {b}</b><small>{s}</small></div>)}</div>;
}

/** Achievements (como na Cyclopedia do Tibia: pontos, grade em estrelas, secretas escondidas até conquistar) */
function Achievements(props: { st: { done: Record<number, number>; progress: Record<number, [number, number]> } | null; help: boolean }) {
  const [list, setList] = useState<AchDef[] | null>(null);
  const [show, setShow] = useState<'all' | 'done' | 'todo'>('all');
  const [q, setQ] = useState('');
  useEffect(() => { void loadAch().then(setList); }, []);
  if (!list || !props.st) return <p class="muted cy-empty">Carregando...</p>;
  const done = props.st.done;
  const pts = list.filter((a) => done[a.id]).reduce((t, a) => t + a.points, 0), max = list.reduce((t, a) => t + a.points, 0);
  const got = list.filter((a) => done[a.id]).length;
  const rows = list.filter((a) => (show === 'all' || (show === 'done') === !!done[a.id]) && (!q || (done[a.id] || !a.secret) && a.name.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (done[b.id] ?? 0) - (done[a.id] ?? 0) || a.grade - b.grade || a.name.localeCompare(b.name));
  return (
    <div class="cy-tabpage">
      {(props.help || !got) && <Steps items={[
        ['🗺️', 'Jogue', 'Caçar, subir level, completar o bestiário, quests e bosses liberam conquistas.'],
        ['⭐', 'Grau', 'As estrelas mostram a dificuldade; quanto mais estrelas, mais pontos.'],
        ['🔒', 'Secretas', 'Algumas ficam escondidas até você conquistar. Continue explorando!'],
      ]} />}
      <div class="cy-ach-top">
        <img src="/assets/interface/images/icon-character-achievement.png" alt="" />
        <div class="cy-ach-score">
          <span>Achievement Points</span>
          <b>{n(pts)}<em> / {n(max)}</em></b>
        </div>
        <div class="cy-meter gold" title={`${got} de ${list.length} conquistados`}>
          <i style={{ width: (max ? (pts / max) * 100 : 0) + '%' }} /><span>{n(got)} de {n(list.length)} conquistados</span>
        </div>
        <select value={show} onChange={(e) => setShow((e.target as HTMLSelectElement).value as never)}>
          <option value="all">Todos</option><option value="done">Conquistados</option><option value="todo">Faltando</option>
        </select>
        <input placeholder="Buscar" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
      </div>
      <div class="cy-ach-list">
        {rows.map((a) => {
          const at = done[a.id], hide = a.secret && !at, pr = props.st!.progress[a.id];
          return (
            <div key={a.id} class={'cy-ach' + (at ? ' on' : hide ? ' secret' : '')}>
              <span class="cy-ach-ico"><img src="/assets/interface/images/icon-achievement.png" alt="" />{at ? <em>✓</em> : hide ? <em>?</em> : null}</span>
              <div class="cy-ach-txt">
                <b>{hide ? 'Achievement secreto' : a.name}</b>
                <small>{hide ? 'Continue jogando para descobrir.' : a.desc_pt}</small>
                {!at && !hide && pr && pr[1] > 1 && <div class="cy-meter thin"><i style={{ width: `${Math.min(100, (pr[0] / pr[1]) * 100)}%` }} /><span>{n(Math.min(pr[0], pr[1]))} / {n(pr[1])}</span></div>}
              </div>
              <div class="cy-ach-side">
                <span class="cy-ach-grade">{Array.from({ length: Math.max(1, a.grade) }, (_, i) => <img key={i} src={`/assets/interface/images/icon-star-${at ? 'gold' : 'inactive'}.png`} alt="" />)}</span>
                <small class="cy-ach-pts">{a.points} {a.points === 1 ? 'ponto' : 'pontos'}</small>
                {at && <small class="muted">{new Date(at).toLocaleDateString('pt-BR')}</small>}
              </div>
            </div>
          );
        })}
        {!rows.length && <p class="muted cy-empty">Nenhuma conquista com esse filtro.</p>}
      </div>
    </div>
  );
}

const TAB_INFO: Record<Tab, [string, string, string]> = {
  bestiary: ['📖', 'Bestiário', 'Cada abate escreve mais uma linha sobre a criatura. Complete as três etapas.'],
  charms: ['🔮', 'Charms', 'Amuletos comprados com pontos do bestiário. Cada um vale contra uma criatura.'],
  cards: ['🃏', 'Cartas', 'O álbum das criaturas dominadas.'],
  achievements: ['🏆', 'Achievements', 'O registro dos seus feitos em Noxara.'],
  animus: ['💠', 'Animus Mastery', 'Almas vencidas no Soul Pit, com bônus de XP permanente.'],
};

export function Cyclopedia(props: {
  data: BestiaryEntry[] | null; kills: Record<string, number>; charms: CharmState; removeGold: number;
  send: (op: 'unlock' | 'assign' | 'remove' | 'card' | 'uncard', id: string, monster?: string) => void; level: number; tab?: Tab;
  ach?: { done: Record<number, number>; progress: Record<number, [number, number]> } | null; onAchOpen?: () => void;
  animus?: string[];
}) {
  const [tab, setTab] = useState<Tab>(props.tab ?? 'bestiary');
  const [help, setHelp] = useState(false);
  const done = props.data ? props.data.filter((d) => bestiaryStage(props.kills[d.name] ?? 0, d) === 3).length : 0;
  const info = TAB_INFO[tab];
  const go = (t: Tab) => { setTab(t); setHelp(false); if (t === 'achievements') props.onAchOpen?.(); };
  const TABS: Array<[Tab, string]> = [['bestiary', 'Bestiário'], ['charms', 'Charms'], ['cards', 'Cards'], ['achievements', 'Achievements'], ['animus', 'Animus Mastery']];
  return (
    <div class="cy">
      {tab !== 'cards' && (
        <div class="cy-head">
          <div class="cy-tome" aria-hidden="true"><span>{info[0]}</span></div>
          <div class="cy-head-txt">
            <div class="cy-title">Cyclopedia · {info[1]}
              <button class={'cy-help' + (help ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
            <div class="cy-sub">{info[2]}</div>
          </div>
          <div class="cy-head-stats">
            {props.data && <div class="cy-stat" title="Criaturas com as três etapas do bestiário"><small>Completas</small><b>{n(done)}<em>/{n(props.data.length)}</em></b></div>}
            <div class="cy-stat charm" title="Pontos de charm para liberar e melhorar amuletos"><small>Pontos de charm</small><b>{n(props.charms.points)}</b></div>
          </div>
        </div>
      )}
      <div class="cy-tabs">
        {TABS.map(([t, label]) => <button key={t} class={'cy-tab' + (tab === t ? ' on' : '')} onClick={() => go(t)}>{label}</button>)}
        {tab === 'cards' && <span class="cy-tabs-pts">Pontos de charm: <b>{n(props.charms.points)}</b></span>}
      </div>
      <div class={'cy-page' + (tab === 'cards' ? ' raw' : '')}>
        {tab === 'animus' ? <Animus list={props.animus ?? []} data={props.data} help={help} />
          : tab === 'achievements' ? <Achievements st={props.ach ?? null} help={help} />
          : !props.data ? <p class="muted cy-empty">Carregando...</p>
          : tab === 'bestiary' ? <Bestiary {...props} data={props.data} help={help} />
          : tab === 'charms' ? <Charms {...props} data={props.data} help={help} />
          : <Cards {...props} data={props.data} />}
      </div>
    </div>
  );
}

/** Três medalhas (bronze, prata, ouro) das etapas do bestiário */
function Medals(props: { st: number; big?: boolean }) {
  return <span class={'cy-medals' + (props.big ? ' big' : '')}>{[1, 2, 3].map((i) => <i key={i} class={'m' + i + (props.st >= i ? ' on' : '')}>{props.big ? STAGE_ROMAN[i - 1] : ''}</i>)}</span>;
}

function Bestiary(props: { data: BestiaryEntry[]; kills: Record<string, number>; charms: CharmState; help: boolean }) {
  const classes = useMemo(() => [...new Set(props.data.map((d) => d.class))].sort(), [props.data]);
  const [cls, setCls] = useState('');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const list = props.data.filter((d) => (!cls || d.class === cls) && (!q || d.name.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => a.stars - b.stars || a.name.localeCompare(b.name));
  const stages = props.data.map((d) => bestiaryStage(props.kills[d.name] ?? 0, d));
  const s1 = stages.filter((s) => s >= 1).length, s2 = stages.filter((s) => s >= 2).length, s3 = stages.filter((s) => s === 3).length;
  const anyKill = props.data.some((d) => (props.kills[d.name] ?? 0) > 0);
  const cur = props.data.find((d) => d.name === sel);
  return (
    <div class="cy-tabpage">
      {(props.help || !anyKill) && <Steps items={[
        ['⚔️', 'Cace', 'Cada abate conta para a criatura. Escolha uma na lista para ver o progresso.'],
        ['🥉', `${n(BESTIARY_KILLS[0])} abates`, `Medalha de bronze: vida e experiência da criatura e +${pct(BESTIARY_XP_PCT)} de XP global.`],
        ['🥈', `${n(BESTIARY_KILLS[1])} abates`, `Medalha de prata: o loot, a carta da criatura e +${pct(BESTIARY_GOLD_PCT)} de gold global.`],
        ['🥇', `${n(BESTIARY_KILLS[2])} abates`, 'Medalha de ouro: pontos de charm e a criatura pode receber charms.'],
      ]} />}
      <div class="cy-bst-sum">
        <span class="cy-chip"><i class="cy-dot m1" />Bronze <b>{n(s1)}</b></span>
        <span class="cy-chip"><i class="cy-dot m2" />Prata <b>{n(s2)}</b></span>
        <span class="cy-chip"><i class="cy-dot m3" />Ouro <b>{n(s3)}</b></span>
        <span class="cy-chip bonus" title="Bônus global somado pelas etapas do bestiário">XP global <b>+{pct(s1 * BESTIARY_XP_PCT)}</b></span>
        <span class="cy-chip bonus" title="Bônus global somado pelas etapas do bestiário">Gold global <b>+{pct(s2 * BESTIARY_GOLD_PCT)}</b></span>
      </div>
      <div class="cy-bst">
        <div class="cy-bst-side">
          <div class="cy-filters">
            <select value={cls} onChange={(e) => setCls((e.target as HTMLSelectElement).value)}>
              <option value="">Todas as classes</option>{classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Procurar..." value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          </div>
          <div class="cy-bst-grid">
            {list.map((d) => {
              const k = props.kills[d.name] ?? 0, st = bestiaryStage(k, d);
              return (
                <button key={d.name} class={'cy-bt st' + st + (sel === d.name ? ' on' : '')} title={st > 0 ? `${d.name} · ${n(k)} abates` : `??? · ${n(k)} abates`} onClick={() => setSel(d.name)}>
                  <span class="cy-bt-face">{st > 0 ? <Portrait outfit={d.outfit} size={36} /> : <span class="cy-unknown">?</span>}</span>
                  <small>{st > 0 ? d.name : '???'}</small>
                  <Medals st={st} />
                  <span class="cy-bt-bar"><i style={{ width: `${Math.min(100, (k / BESTIARY_KILLS[2]) * 100)}%` }} /></span>
                </button>
              );
            })}
            {!list.length && <p class="muted cy-empty">Nenhuma criatura encontrada.</p>}
          </div>
        </div>
        <div class="cy-bst-detail">
          {!cur ? <div class="cy-bst-none"><span>📜</span><p class="muted small">Escolha uma criatura. Matar libera as informações em três etapas, como no Tibia.</p></div> : (() => {
            const k = props.kills[cur.name] ?? 0, st = bestiaryStage(k, cur);
            const next = st < 3 ? BESTIARY_KILLS[st]! : 0, prev = st > 0 ? BESTIARY_KILLS[st - 1]! : 0;
            const charm = Object.entries(props.charms.assigned).filter(([, m]) => m === cur.name).map(([id]) => CHARMS.find((c) => c.id === id)?.name).filter(Boolean);
            return <>
              <b class="cy-bst-name">{st > 0 ? cur.name : 'Criatura desconhecida'}</b>
              <span class={'cy-bst-face st' + st}>{st > 0 ? <Portrait outfit={cur.outfit} size={64} /> : <span class="cy-unknown big">?</span>}</span>
              <small class="cy-bst-cls">{cur.class} · <span class="cy-stars">{'★'.repeat(cur.stars)}<em>{'☆'.repeat(Math.max(0, 5 - cur.stars))}</em></span></small>
              <span class={'cy-rank st' + st}>{STAGE_NAME[st]}</span>
              <Medals st={st} big />
              <div class="cy-meter"><i style={{ width: `${Math.min(100, (k / BESTIARY_KILLS[2]) * 100)}%` }} /><span>{n(k)} / {n(BESTIARY_KILLS[2])} abates</span></div>
              {st < 3 ? <small class="cy-next">Próxima medalha ({STAGE_NAME[st + 1]}): faltam <b>{n(next - k)}</b> abates
                <span class="cy-meter thin"><i style={{ width: `${Math.min(100, ((k - prev) / (next - prev)) * 100)}%` }} /></span></small>
                : <small class="cy-next done">Criatura completa! +{n(cur.points)} pontos de charm.</small>}
              <div class="cy-unlocks">
                <div class={st >= 1 ? 'on' : ''}><i class="cy-dot m1" /><span><b>{n(BESTIARY_KILLS[0])}</b> +{pct(BESTIARY_XP_PCT)} de XP global</span></div>
                <div class={st >= 2 ? 'on' : ''}><i class="cy-dot m2" /><span><b>{n(BESTIARY_KILLS[1])}</b> +{pct(BESTIARY_GOLD_PCT)} de gold global e a carta</span></div>
                <div class={st >= 3 ? 'on' : ''}><i class="cy-dot m3" /><span><b>{n(BESTIARY_KILLS[2])}</b> {n(cur.points)} pontos de charm</span></div>
              </div>
              {st >= 1 ? <div class="cy-kv"><span>Vida <b>{n(cur.hp)}</b></span><span>Experiência <b>{n(cur.exp)}</b></span></div>
                : <small class="muted">Vida e experiência aparecem na medalha de bronze.</small>}
              {st >= 2 ? <div class="cy-loot">{cur.loot.map((id) => <span key={id} class="item-slot"><ItemImg typeId={id} count={1} /></span>)}</div>
                : <small class="muted">O loot aparece na medalha de prata.</small>}
              {st === 3 && <small class="cy-charm-on">🔮 Charms nesta criatura: <b>{charm.length ? charm.join(', ') : 'nenhum'}</b></small>}
            </>;
          })()}
        </div>
      </div>
    </div>
  );
}

/** emoji e cor do amuleto de cada charm */
const EL_GEM: Record<string, [string, string]> = {
  physical: ['🩸', '#c84a3a'], fire: ['🔥', '#ff7a1c'], earth: ['☠️', '#5ac83a'], ice: ['❄️', '#6ad0ff'], energy: ['⚡', '#c07aff'], death: ['💀', '#9a6ab0'], holy: ['✨', '#ffe06a'],
};
const FX_GEM: Record<string, [string, string]> = {
  overpower: ['💪', '#e05a3a'], overflux: ['🌀', '#4a8aff'], carnage: ['💥', '#ff5030'], parry: ['🛡️', '#c0c0c8'], dodge: ['💨', '#a0e0d0'], lowblow: ['🎯', '#ff9a3a'],
  savage: ['🗡️', '#e03a3a'], lifeleech: ['❤️', '#e0405a'], manaleech: ['💧', '#4a90ff'], cripple: ['🕸️', '#a0a0a0'], numb: ['🧊', '#80c0e0'], adrenaline: ['🏃', '#ffd040'],
  cleanse: ['🌿', '#60d080'], bless: ['🙏', '#fff0b0'], gut: ['💰', '#f0c040'], fatalhold: ['⛓️', '#a08060'],
};
const gem = (c: CharmDef) => (c.effect === 'elemental' ? EL_GEM[c.element ?? ''] : FX_GEM[c.effect]) ?? ['🔮', '#c0a0ff'];
const MINOR = new Set(['vampiric', 'voids', 'cripple', 'numb', 'adrenaline', 'cleanse', 'bless', 'gut', 'fatalhold']);

function Charms(props: { data: BestiaryEntry[]; kills: Record<string, number>; charms: CharmState; removeGold: number; help: boolean; send: (op: 'unlock' | 'assign' | 'remove' | 'card' | 'uncard', id: string, monster?: string) => void }) {
  const complete = props.data.filter((d) => bestiaryStage(props.kills[d.name] ?? 0, d) === 3).map((d) => d.name).sort();
  const [pick, setPick] = useState<Record<string, string>>({});
  const [kind, setKind] = useState<'all' | 'major' | 'minor' | 'mine'>('all');
  const owned = CHARMS.filter((c) => (props.charms.levels[c.id] ?? 0) > 0).length;
  const placed = Object.keys(props.charms.assigned).length;
  const list = CHARMS.filter((c) => kind === 'all' || (kind === 'mine' ? (props.charms.levels[c.id] ?? 0) > 0 : (kind === 'minor') === MINOR.has(c.id)));
  return (
    <div class="cy-tabpage">
      {(props.help || !owned) && <Steps items={[
        ['🥇', 'Pontos', 'Completar uma criatura no bestiário (medalha de ouro) dá pontos de charm.'],
        ['🔮', 'Liberar', 'Gaste pontos para liberar um amuleto; melhore até o grau 3.'],
        ['🎯', 'Colocar', 'Cada charm vai numa criatura completa e só funciona contra ela.'],
        ['🪙', 'Trocar', `Tirar um charm de uma criatura custa ${n(props.removeGold)} gp.`],
      ]} />}
      <div class="cy-ch-top">
        <span class="cy-chip">Liberados <b>{owned}/{CHARMS.length}</b></span>
        <span class="cy-chip">Em uso <b>{placed}</b></span>
        <span class="cy-chip">Criaturas completas <b>{complete.length}</b></span>
        <span class="cy-chip">Tirar <b>{n(props.removeGold)} gp</b></span>
        <select value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as never)}>
          <option value="all">Todos</option><option value="major">Maiores</option><option value="minor">Menores</option><option value="mine">Liberados</option>
        </select>
      </div>
      <div class="cy-ch-list">
        {list.map((c) => {
          const lv = props.charms.levels[c.id] ?? 0, on = props.charms.assigned[c.id];
          const unit = c.effect === 'savage' ? '% de dano crítico' : c.effect === 'lifeleech' || c.effect === 'manaleech' ? '% de roubo' : c.effect === 'bless' ? '% menos perda' : c.effect === 'gut' ? '% de loot' : '% de chance';
          const [emo, col] = gem(c);
          const cost = lv < 3 ? c.cost[lv]! : 0, miss = cost - props.charms.points;
          return (
            <div key={c.id} class={'cy-ch' + (lv ? ' own' : ' locked') + (on ? ' placed' : '') + (lv === 3 ? ' max' : '')} style={{ '--gem': col } as never}>
              <div class="cy-amulet" title={lv ? `Grau ${lv}` : 'Bloqueado'}><span>{emo}</span>{!lv && <em>🔒</em>}</div>
              <div class="cy-ch-txt">
                <div class="cy-ch-name"><b>{c.name}</b>
                  <span class="cy-pips">{[1, 2, 3].map((i) => <i key={i} class={lv >= i ? 'on' : ''} />)}</span>
                  <span class="cy-ch-lv">{lv ? `grau ${lv}` : 'bloqueado'}</span></div>
                <small>{c.desc}</small>
                <div class="cy-grades">{c.value.map((v, i) => <span key={i} class={lv === i + 1 ? 'cur' : lv > i ? 'got' : ''}>{String(v).replace('.', ',')}{unit}</span>)}</div>
                {on && <small class="cy-ch-on">🎯 Em: <b>{on}</b></small>}
              </div>
              <div class="cy-ch-btns">
                {lv < 3 && <>
                  <button class="tb-button green" disabled={props.charms.points < cost} onClick={() => props.send('unlock', c.id)}>{lv ? 'Melhorar' : 'Liberar'} ({n(cost)})</button>
                  {miss > 0 && <small class="cy-miss">faltam {n(miss)} pontos</small>}
                </>}
                {lv === 3 && <small class="cy-maxed">Grau máximo</small>}
                {lv > 0 && !on && <>
                  <select value={pick[c.id] ?? ''} onChange={(e) => setPick({ ...pick, [c.id]: (e.target as HTMLSelectElement).value })}>
                    <option value="">{complete.length ? 'Criatura...' : 'Nenhuma criatura completa'}</option>
                    {complete.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <button class="tb-button" disabled={!pick[c.id]} onClick={() => props.send('assign', c.id, pick[c.id])}>Colocar</button>
                </>}
                {on && <button class="tb-button" onClick={() => props.send('remove', c.id)}>Tirar</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Animus Mastery: criaturas dominadas no Soul Pit (Soul Cores caem raramente nas hunts; use-os pelo botão direito). */
function Animus(props: { list: string[]; data: BestiaryEntry[] | null; help: boolean }) {
  const gen = animusGeneral(props.list.length);
  const byName = new Map((props.data ?? []).map((e) => [e.name.toLowerCase(), e]));
  return (
    <div class="cy-tabpage">
      <div class="cy-an-hero">
        <div class="cy-an-orb"><b>+{String(gen).replace('.', ',')}%</b><small>XP geral</small></div>
        <div class="cy-an-txt">
          <span><b>{props.list.length}</b> {props.list.length === 1 ? 'Animus Mastery' : 'Animus Masteries'}</span>
          <small>Cada mastery dá <b>+{ANIMUS_XP_PCT}%</b> de XP ao caçar aquela criatura e <b>+{String(ANIMUS_GENERAL_PCT).replace('.', ',')}%</b> de XP geral, sem limite.</small>
        </div>
      </div>
      {(props.help || !props.list.length) && <Steps items={[
        ['💎', 'Soul Core', 'Os monstros das hunts dropam, raramente, o Soul Core deles.'],
        ['🖱️', 'Soul Pit', 'Clique com o botão direito no Soul Core e escolha "Usar no Soul Pit".'],
        ['🌊', '4 ondas', 'Enfrente a criatura fortalecida: 10, 20 e 30 monstros; na última, o campeão com mais 40. XP e loot normais por monstro.'],
        ['💠', 'Mastery', `Vencendo, ganha +${ANIMUS_XP_PCT}% de XP contra ela e +${String(ANIMUS_GENERAL_PCT).replace('.', ',')}% de XP geral.`],
      ]} />}
      <div class="cy-an-grid">
        {props.list.map((nm) => {
          const e = byName.get(nm);
          return <div key={nm} class="cy-an-card"><span class="cy-an-face">{e ? <Portrait outfit={e.outfit} size={32} /> : <span class="cy-unknown">💠</span>}</span>
            <span class="cy-an-name">{e?.name ?? nm}</span><small>+{ANIMUS_XP_PCT}% XP</small></div>;
        })}
        {!props.list.length && <p class="muted cy-empty">Nenhuma Animus Mastery ainda. O primeiro Soul Core está esperando numa hunt.</p>}
      </div>
    </div>
  );
}
